import { differenceInDays, isAfter } from "date-fns";
import {
  GetSurveyDispatchResponse,
  GetSurveyDispatchesResponse,
  SurveyDispatchReminder,
  SurveyDispatchStatus,
  SurveyRecipient,
} from "../types/survey";
import { isSurveyClosed } from "../utils/WorkplaceCulture.utils";
import { getFormattedDate } from "../utils/formatters";

class SurveyDispatch {
  surveyId: string;
  id: string;
  name: string;
  dueDate?: Date;
  status: SurveyDispatchStatus;
  engagement: number;
  launchDate?: Date;
  reminders: SurveyDispatchReminder[];
  recipients: SurveyRecipient[];
  recipientsCount: number;
  isJobRolesMapped: boolean;
  isPublic: boolean;
  score: number | null;

  constructor(
    parameters: GetSurveyDispatchResponse | GetSurveyDispatchesResponse
  ) {
    this.id = parameters.id;
    this.dueDate = parameters.due_date
      ? new Date(parameters.due_date)
      : undefined;
    this.engagement = parameters.engagement || 0;
    this.launchDate = parameters.launch_date
      ? new Date(parameters.launch_date)
      : undefined;
    this.surveyId = parameters.survey_id;
    this.reminders =
      parameters.reminders?.map((reminder) => ({
        ...reminder,
        date: new Date(reminder.date),
        isFinal: reminder.is_final || false,
      })) || [];
    this.name = parameters.name;
    this.recipients = "recipients" in parameters ? parameters.recipients : [];
    this.recipientsCount = parameters.recipients_count || 0;
    this.isJobRolesMapped = parameters.has_job_roles;
    this.isPublic = parameters.is_public;
    this.score = parameters.total_score;
    if (this.isInProgress()) {
      this.status = SurveyDispatchStatus.InProgress;
    } else if (this.isReady()) {
      this.status = SurveyDispatchStatus.Ready;
    } else if (this.isActive()) {
      this.status = SurveyDispatchStatus.Active;
    } else {
      this.status = SurveyDispatchStatus.Latest;
    }
  }

  getStatusLabel = (hasError: boolean) => {
    if (hasError || !this.status) return "error";

    return this.status;
  };

  getStatusDateFormatted = (t: (str: string) => string, language: string) => {
    if (this.isReady() && this.launchDate) {
      return getFormattedDate(this.launchDate, t, language);
    } else {
      return this.dueDate && getFormattedDate(this.dueDate, t, language);
    }
  };

  getProgress = (): number => {
    if (this.launchDate && this.dueDate) {
      if (!this.isActive()) {
        return 100;
      }

      const daysBetweenLaunchAndDueDate = differenceInDays(
        this.dueDate,
        this.launchDate
      );

      const daysSinceLaunched = differenceInDays(
        new Date(Date.now()),
        this.launchDate
      );

      const percentageDone =
        (daysSinceLaunched / daysBetweenLaunchAndDueDate) * 100;

      return Math.round(percentageDone);
    }

    return 0;
  };

  getReminder = (id: number | null) => {
    return this.reminders?.find((reminder) => reminder.id === id);
  };

  getEngagementPercentage = () => {
    return this.recipientsCount > 0
      ? Math.round((this.engagement / this.recipientsCount) * 100)
      : 0;
  };

  isReminderDisabled = (reminderId: number) => {
    const dispatchReminder = this.getReminder(reminderId);
    if (dispatchReminder?.date) {
      return isAfter(Date.now(), dispatchReminder.date);
    }
    return false;
  };

  // Survey has been launched and not closed
  isActive = () => {
    if (this.isInProgress()) {
      return false;
    } else if (this.launchDate && this.dueDate) {
      return (
        isAfter(this.dueDate, new Date(Date.now())) &&
        isAfter(new Date(Date.now()), this.launchDate)
      );
    }
    return false;
  };

  // Survey does not have launch date or due date or recipients
  isInProgress = () => {
    if (!this.launchDate || !this.dueDate) {
      return true;
    }

    if (isAfter(new Date(Date.now()), this.dueDate)) {
      return false;
    }

    return (
      (this.recipientsCount === 0 && !this.isPublic) || !this.isJobRolesMapped
    );
  };

  // Survey has all the required fields set and is ready to be launched
  isReady = () => {
    if (this.isInProgress()) {
      return false;
    } else if (this.launchDate) {
      return isAfter(this.launchDate, new Date(Date.now()));
    }
    return false;
  };

  hasNotLaunched = () => {
    return this.isReady() || this.isInProgress();
  };

  isClosed = () => {
    return isSurveyClosed(this.dueDate);
  };

  setLaunchDate = (launchDate: Date) => {
    this.launchDate = launchDate;
  };

  setDueDate = (dueDate: Date) => {
    this.dueDate = dueDate;
  };

  setReminders = (reminders: SurveyDispatchReminder[]) => {
    this.reminders = reminders;
  };

  setRecipients = (recipients: SurveyRecipient[]) => {
    this.recipients = recipients;
  };

  setIsJobRolesMapped = (isJobRolesMapped: boolean) => {
    this.isJobRolesMapped = isJobRolesMapped;
  };

  hasData = () => {
    return this.score !== null;
  };
}

export default SurveyDispatch;
